/* eslint-disable no-unused-vars */
import {
  AlertTriangle,
  ArrowUpRight,
  Bot,
  ChevronDown,
  CirclePlay,
  GitMerge,
  Link2,
  MoreHorizontal,
  RefreshCw,
  Search,
  UserRound,
  X
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { CindroOrgMember } from '@cindro/contracts';
import {
  claimWorkIdentity,
  createWorkItemRelationship,
  deleteWorkItemRelationship,
  dispatchPipelineStage,
  getClickupConnection,
  listOrganizationMembers,
  reconcileClickup,
  recordJourneyEvidence,
  remediateWorkJourney,
  triageWorkJourneyAttention,
  type WorkJourneyRow,
  type ClickupConnectionRow
} from '../api';
import { EmptyState } from '../components/EmptyState';
import { SectionCard } from '../components/SectionCard';
import { JourneyDetail } from '../components/JourneyDetail';
import { JourneyResultRow } from '../components/JourneyResultRow';
import { DeliveryCoveragePanel } from '../components/PipelineTrackEditor';
import { useFeedback } from '../contexts/FeedbackContext';
import { useSession } from '../contexts/SessionContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatRelativeDate } from '../utils/formatters';
import {
  actionLinkLabel,
  actionRecommendation,
  actionStateLabel,
  formatAttentionDetail
} from '../utils/journey-action-copy';
import { legacyJourney } from '../utils/legacy-journey';
import { buildJourneyRadarItem } from '../utils/journey-radar';
import {
  defaultWorkInboxView,
  workAttentionRank,
  type WorkRadarView,
  type WorkInboxView
} from '../utils/work-radar';
import {
  countWorkInboxItems,
  responsibilityAssignsWork,
  responsibilityName,
  selectWorkInboxItems
} from '../utils/work-inbox';
import { summarizeWorkRadar } from '../utils/work-radar-summary';
import { filterWorkRadarItems } from '../utils/work-radar-filter';

function PipelineRunsPage({ mode = 'radar' }: { mode?: 'radar' | 'inbox' }) {
  const [searchParams] = useSearchParams();
  const { session } = useSession();
  const accessToken = session?.accessToken;
  const { workspace, activeOrgId, setWorkspaceVersion } = useWorkspace();
  const { toast, confirm } = useFeedback();
  const { workJourneys, pipelineRuns, stageRuns, apps, pipelineDefinitions } =
    workspace;
  const [query, setQuery] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [stageFilter, setStageFilter] = useState('');
  const [view, setView] = useState<WorkRadarView>('all');
  const [inboxView, setInboxView] = useState<WorkInboxView>('mine');
  const [expandedJourneyId, setExpandedJourneyId] = useState('');
  const [visibleLimit, setVisibleLimit] = useState(12);
  const [dispatchingKey, setDispatchingKey] = useState('');
  const [triagingFindingId, setTriagingFindingId] = useState('');
  const [remediatingItemId, setRemediatingItemId] = useState('');
  const [clickupConnection, setClickupConnection] =
    useState<ClickupConnectionRow | null>(null);
  const [organizationMembers, setOrganizationMembers] = useState<
    CindroOrgMember[]
  >([]);
  const [reconcilingClickup, setReconcilingClickup] = useState(false);
  const [claimingIdentityId, setClaimingIdentityId] = useState('');
  const initialInboxScope = useRef('');

  useEffect(() => {
    if (!accessToken) return;
    let active = true;
    setClickupConnection(null);
    getClickupConnection(accessToken, activeOrgId)
      .then((connection) => {
        if (active) setClickupConnection(connection);
      })
      .catch(() => {
        if (active) setClickupConnection(null);
      });
    return () => {
      active = false;
    };
  }, [accessToken, activeOrgId]);

  useEffect(() => {
    if (!accessToken || !activeOrgId || mode !== 'inbox') return;
    let active = true;
    listOrganizationMembers(accessToken, activeOrgId)
      .then((members) => {
        if (active)
          setOrganizationMembers(members.filter((member) => member.isActive));
      })
      .catch(() => {
        if (active) setOrganizationMembers([]);
      });
    return () => {
      active = false;
    };
  }, [accessToken, activeOrgId, mode]);

  const journeys = useMemo(() => {
    if (workJourneys.length > 0) return workJourneys;
    return pipelineRuns.map((run) =>
      legacyJourney(
        run,
        stageRuns.filter((stage) => stage.pipelineRunId === run.pipelineRunId)
      )
    );
  }, [pipelineRuns, stageRuns, workJourneys]);

  const taskItems = useMemo(
    () =>
      journeys.flatMap((journey) =>
        journey.items
          .filter((item) => item.kind === 'task')
          .map((item) => ({
            ...item,
            responsibilities: journey.responsibilities.filter(
              (responsibility) =>
                responsibility.workJourneyItemId === item.workJourneyItemId
            )
          }))
      ),
    [journeys]
  );

  const radarItems = useMemo(
    () =>
      journeys
        .map((journey) => buildJourneyRadarItem(journey))
        .sort((left, right) => {
          const attention =
            workAttentionRank(left.attentionLevel) -
            workAttentionRank(right.attentionLevel);
          return (
            attention ||
            right.journey.lastActivityAt.localeCompare(
              left.journey.lastActivityAt
            )
          );
        }),
    [journeys]
  );

  const visibleItems = useMemo(
    () =>
      filterWorkRadarItems(radarItems, {
        projectId: projectFilter,
        query,
        stageKey: stageFilter,
        view
      }),
    [projectFilter, query, radarItems, stageFilter, view]
  );

  const linkedJourneyId = mode === 'radar' ? searchParams.get('journey') : null;

  useEffect(() => {
    if (!linkedJourneyId) return;
    if (view !== 'all' || projectFilter !== 'all' || query || stageFilter) {
      setView('all');
      setProjectFilter('all');
      setQuery('');
      setStageFilter('');
      return;
    }
    const journeyIndex = visibleItems.findIndex(
      (item) => item.journey.workJourneyId === linkedJourneyId
    );
    if (journeyIndex < 0) return;
    setExpandedJourneyId(linkedJourneyId);
    setVisibleLimit((limit) => Math.max(limit, journeyIndex + 1));
    const scrollTimer = window.setTimeout(() => {
      document
        .getElementById(`journey-${linkedJourneyId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => window.clearTimeout(scrollTimer);
  }, [linkedJourneyId, projectFilter, query, stageFilter, view, visibleItems]);

  useEffect(() => {
    setVisibleLimit(12);
  }, [projectFilter, query, stageFilter, view]);

  const radarSummary = summarizeWorkRadar(radarItems);
  const displayedItems = visibleItems.slice(0, visibleLimit);
  const allActionItems = radarItems.filter((item) => {
    if (!item.nextAction) return false;
    if (
      projectFilter !== 'all' &&
      !item.journey.projectIds.includes(projectFilter)
    )
      return false;
    const normalizedQuery = query.trim().toLowerCase();
    return !normalizedQuery || item.searchText.includes(normalizedQuery);
  });
  const inboxUser = {
    id: session?.user.tenantUserId,
    email: session?.user.email
  };
  const inboxCounts = countWorkInboxItems(allActionItems, inboxUser);
  const suggestedInboxView = defaultWorkInboxView(inboxCounts);
  const clickupFullCheckAt =
    clickupConnection?.lifecycle.reconciliation?.completedAt ||
    clickupConnection?.lastSyncAt;
  const clickupCheckAge = clickupFullCheckAt
    ? Date.now() - new Date(clickupFullCheckAt).getTime()
    : Number.POSITIVE_INFINITY;
  const clickupCheckIsOld = clickupCheckAge > 24 * 60 * 60 * 1000;

  useEffect(() => {
    if (
      mode !== 'inbox' ||
      allActionItems.length === 0 ||
      initialInboxScope.current === activeOrgId
    )
      return;
    setInboxView(suggestedInboxView);
    initialInboxScope.current = activeOrgId;
  }, [activeOrgId, allActionItems.length, mode, suggestedInboxView]);
  const scopedActionItems =
    mode === 'inbox'
      ? selectWorkInboxItems(allActionItems, inboxView, inboxUser)
      : allActionItems;
  const unmappedIdentities = [
    ...new Map(
      allActionItems
        .flatMap((item) => item.nextAction?.responsibilities || [])
        .map((responsibility) => responsibility.identity)
        .filter((identity): identity is NonNullable<typeof identity> =>
          Boolean(identity && !identity.isLinked)
        )
        .map((identity) => [identity.workIdentityId, identity])
    ).values()
  ];
  const stageFilterLabel =
    radarItems
      .flatMap((item) => item.stages)
      .find((stage) => stage.key === stageFilter)?.label || stageFilter;

  async function handleAcknowledge(
    journey: WorkJourneyRow,
    stageKey: string,
    label: string
  ) {
    if (!session) return;
    const accepted = await confirm({
      title: `Complete ${label}?`,
      message:
        'This records auditable human evidence in Cindro. It does not trigger a deployment or change an external system.',
      confirmLabel: 'Record completion'
    });
    if (!accepted) return;
    try {
      await recordJourneyEvidence(session.accessToken, {
        workJourneyId: journey.workJourneyId,
        stageKey,
        status: 'success',
        note: `${label} completed manually`
      });
      setWorkspaceVersion((version) => version + 1);
      toast('success', `${label} recorded`);
    } catch (error: any) {
      toast(
        'error',
        error?.response?.data?.message ||
          error?.message ||
          'Unable to record evidence.'
      );
    }
  }

  async function handleCreateTaskRelationship(
    sourceWorkJourneyItemId: string,
    targetWorkJourneyItemId: string
  ): Promise<boolean> {
    if (!session) return false;
    try {
      await createWorkItemRelationship(
        session.accessToken,
        sourceWorkJourneyItemId,
        targetWorkJourneyItemId
      );
      setWorkspaceVersion((version) => version + 1);
      toast('success', 'Execution task linked');
      return true;
    } catch (error: any) {
      toast(
        'error',
        error?.response?.data?.message ||
          error?.message ||
          'Could not link those tasks'
      );
      return false;
    }
  }

  async function handleDeleteTaskRelationship(relationshipId: string) {
    if (!session) return;
    try {
      await deleteWorkItemRelationship(session.accessToken, relationshipId);
      setWorkspaceVersion((version) => version + 1);
      toast('success', 'Task link removed');
    } catch (error: any) {
      toast(
        'error',
        error?.response?.data?.message ||
          error?.message ||
          'Could not remove the task link'
      );
    }
  }

  async function handleDispatch(
    journey: WorkJourneyRow,
    stageKey: string,
    label: string
  ) {
    if (!session) return;
    const expectation = journey.expectations.find(
      (item) => item.stageKey === stageKey
    );
    const definition = pipelineDefinitions.find(
      (item) => item.pipelineDefinitionId === expectation?.pipelineDefinitionId
    );
    const stage = definition?.stages.find((item) => item.stageKey === stageKey);
    if (!stage?.connectorType || !stage.connectorAction) return;
    const config = stage.actionConfig || {};
    const sourceStage =
      typeof config['sourceStage'] === 'string' ? config['sourceStage'] : '';
    const sourceExpectation = journey.expectations.find(
      (item) => item.stageKey === sourceStage
    );
    const sourceSignal = journey.items.find((item) =>
      sourceExpectation?.observedItemIds.includes(item.workJourneyItemId)
    );
    const target =
      stage.connectorType === 'github' &&
      stage.connectorAction === 'merge_pull_request'
        ? `${sourceSignal?.externalRepo || 'GitHub'} · pull request #${sourceSignal?.externalRefId || 'unknown'}`
        : stage.connectorType === 'github'
          ? `${String(config['repo'] || 'GitHub')} · ${String(config['workflow'] || stage.connectorAction)}`
          : `${stage.connectorType} · ${stage.connectorAction}`;
    const accepted = await confirm({
      title: `Run ${label}?`,
      message: `Cindro will dispatch ${target}. GitHub Actions performs the work; the repository-owned workflow still enforces its branch, confirmation and deployment controls.`,
      confirmLabel: 'Run next step'
    });
    if (!accepted) return;
    setDispatchingKey(`${journey.workJourneyId}:${stageKey}`);
    try {
      const result = await dispatchPipelineStage(session.accessToken, {
        workJourneyId: journey.workJourneyId,
        pipelineDefinitionId: definition?.pipelineDefinitionId,
        stageKey
      });
      toast(
        'success',
        result.status === 'success' ? `${label} dispatched` : `${label} queued`
      );
      setWorkspaceVersion((version) => version + 1);
    } catch (error: unknown) {
      const failure = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      toast(
        'error',
        failure.response?.data?.message ||
          failure.message ||
          `Unable to dispatch ${label}.`
      );
    } finally {
      setDispatchingKey('');
    }
  }

  function inspectJourney(workJourneyId: string) {
    setView('all');
    setStageFilter('');
    setExpandedJourneyId(workJourneyId);
    window.setTimeout(() => {
      document
        .getElementById(`journey-${workJourneyId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 80);
  }

  async function handleTriageFinding(
    findingId: string,
    action:
      | 'assign_self'
      | 'assign_user'
      | 'acknowledge'
      | 'snooze'
      | 'resolve',
    assigneeUserId?: string
  ) {
    if (!session) return;
    if (action === 'resolve') {
      const accepted = await confirm({
        title: 'Resolve this finding?',
        message:
          'Remove it from the operational inbox. It will return if the condition clears and is later detected again.',
        confirmLabel: 'Resolve finding'
      });
      if (!accepted) return;
    }
    setTriagingFindingId(findingId);
    try {
      await triageWorkJourneyAttention(session.accessToken, {
        workJourneyAttentionId: findingId,
        action,
        assigneeUserId,
        snoozeMinutes: action === 'snooze' ? 1440 : undefined
      });
      const assignee = organizationMembers.find(
        (member) => member.tenantUserId === assigneeUserId
      );
      toast(
        'success',
        action === 'assign_self'
          ? 'Finding assigned to you'
          : action === 'assign_user'
            ? `Assigned to ${assignee?.displayName || assignee?.email || 'teammate'}`
            : action === 'acknowledge'
              ? 'Finding acknowledged'
              : action === 'snooze'
                ? 'Finding snoozed for one day'
                : 'Finding resolved'
      );
      setWorkspaceVersion((version) => version + 1);
    } catch (error: unknown) {
      const failure = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      toast(
        'error',
        failure.response?.data?.message ||
          failure.message ||
          'Unable to update this finding.'
      );
    } finally {
      setTriagingFindingId('');
    }
  }

  async function handleRemediation(
    item: ReturnType<typeof buildJourneyRadarItem>
  ) {
    const action = item.nextAction;
    if (
      !session ||
      !action?.evidenceItemId ||
      action.remediation !== 'rerun_failed_jobs'
    )
      return;
    const accepted = await confirm({
      title: `Rerun failed jobs for ${item.journey.title}?`,
      message: `Cindro will ask GitHub to rerun only the failed jobs in the exact workflow run linked to ${action.stageLabel}. Repository protections and workflow-owned controls remain in force.`,
      confirmLabel: 'Rerun failed jobs'
    });
    if (!accepted) return;
    setRemediatingItemId(action.evidenceItemId);
    try {
      await remediateWorkJourney(session.accessToken, {
        workJourneyId: item.journey.workJourneyId,
        workJourneyItemId: action.evidenceItemId,
        action: 'rerun_failed_jobs'
      });
      toast('success', 'GitHub is rerunning the failed jobs');
      setWorkspaceVersion((version) => version + 1);
    } catch (error: unknown) {
      const failure = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      toast(
        'error',
        failure.response?.data?.message ||
          failure.message ||
          'Unable to rerun the failed jobs.'
      );
    } finally {
      setRemediatingItemId('');
    }
  }

  async function handleClickupReconciliation() {
    if (!session || clickupConnection?.status !== 'active') return;
    setReconcilingClickup(true);
    try {
      const reconciliation = await reconcileClickup(
        session.accessToken,
        activeOrgId
      );
      setClickupConnection((current) =>
        current
          ? {
              ...current,
              lastSyncAt: reconciliation.completedAt,
              lifecycle: { ...current.lifecycle, reconciliation }
            }
          : current
      );
      setWorkspaceVersion((version) => version + 1);
      toast(
        'success',
        `Checked ${reconciliation.scanned} recent ClickUp tasks · ${reconciliation.synced} updated`
      );
    } catch (error: unknown) {
      const failure = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      toast(
        'error',
        failure.response?.data?.message ||
          failure.message ||
          'Unable to reconcile ClickUp tasks.'
      );
    } finally {
      setReconcilingClickup(false);
    }
  }

  async function handleClaimIdentity(workIdentityId: string, label: string) {
    if (!session) return;
    const accepted = await confirm({
      title: `Link ${label} to you?`,
      message:
        'Cindro matched the provider email to your signed-in account. Assigned work will appear in My work and the link will be audited.',
      confirmLabel: 'Verify identity'
    });
    if (!accepted) return;
    setClaimingIdentityId(workIdentityId);
    try {
      await claimWorkIdentity(session.accessToken, workIdentityId);
      toast('success', `${label} is now linked to your Cindro identity`);
      setWorkspaceVersion((version) => version + 1);
    } catch (error: unknown) {
      const failure = error as {
        response?: { data?: { message?: string } };
        message?: string;
      };
      toast(
        'error',
        failure.response?.data?.message ||
          failure.message ||
          'Unable to link this provider identity.'
      );
    } finally {
      setClaimingIdentityId('');
    }
  }

  return (
    <>
      {mode === 'radar' ? (
        <DeliveryCoveragePanel
          selectedStage={stageFilter}
          onSelectedStageChange={setStageFilter}
        />
      ) : null}

      <SectionCard
        className={`workRadarPanel workRadarPanel--${mode}`}
        label={mode === 'inbox' ? 'Inbox' : 'Workspace'}
        title={
          mode === 'inbox'
            ? inboxView === 'mine'
              ? 'Assigned to you'
              : inboxView === 'team'
                ? 'Needs attention'
                : inboxView === 'approvals'
                  ? 'Waiting for approval'
                  : 'Needs an owner'
            : 'Development work'
        }
        description={
          mode === 'inbox'
            ? inboxView === 'mine'
              ? `${scopedActionItems.length} open journey${scopedActionItems.length === 1 ? '' : 's'} assigned to you.`
              : inboxView === 'team'
                ? `${scopedActionItems.length} journey${scopedActionItems.length === 1 ? '' : 's'} with an open action across your projects.`
                : inboxView === 'approvals'
                  ? `${scopedActionItems.length} journey${scopedActionItems.length === 1 ? ' is' : 's are'} waiting for a review.`
                  : `${scopedActionItems.length} journey${scopedActionItems.length === 1 ? '' : 's'} have no named owner.`
            : radarItems.length
              ? radarSummary.issueSummary ||
                `${radarSummary.progressing} progressing · no known blockers`
              : 'Follow work from its task through code, CI and production.'
        }
        action={
          radarItems.length ? (
            <span className="workRadarResultCount">
              {mode === 'inbox'
                ? `${scopedActionItems.length} journeys`
                : `${radarItems.length} tracked`}
            </span>
          ) : undefined
        }
      >
        {radarItems.length ? (
          <>
            <div className="workRadarCommandBar">
              <label className="bundleSearch">
                <Search size={15} />
                <input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Search by change, repo, or person"
                  aria-label="Search work radar"
                />
              </label>
              <select
                value={projectFilter}
                onChange={(event) => setProjectFilter(event.target.value)}
                aria-label="Filter work radar by project"
              >
                <option value="all">All projects</option>
                {apps.map((app) => (
                  <option key={app.id} value={app.id}>
                    {apps.some(
                      (candidate) =>
                        candidate.id !== app.id && candidate.name === app.name
                    )
                      ? `${app.name} · ${app.slug}`
                      : app.name}
                  </option>
                ))}
              </select>
              {clickupConnection &&
              ['active', 'error'].includes(clickupConnection.status) ? (
                <div
                  className={`workRadarSyncControl${clickupConnection.status === 'error' ? ' workRadarSyncControl--error' : ''}`}
                >
                  <span
                    className={
                      clickupConnection.status === 'error' || clickupCheckIsOld
                        ? 'is-stale'
                        : ''
                    }
                  >
                    <Link2 size={14} />
                    <span>
                      <strong>
                        {clickupConnection.status === 'error'
                          ? 'ClickUp connection needs attention'
                          : clickupCheckIsOld
                            ? 'ClickUp check is old'
                            : 'ClickUp task links'}
                      </strong>
                      <small>
                        {clickupConnection.errorMessage ||
                          (clickupFullCheckAt
                            ? `Task data checked ${formatRelativeDate(clickupFullCheckAt)}${clickupConnection.lastWebhookAt ? ` · latest task event ${formatRelativeDate(clickupConnection.lastWebhookAt)}` : ''}`
                            : 'No task check recorded yet')}
                      </small>
                    </span>
                  </span>
                  {clickupConnection.status === 'active' ? (
                    <button
                      type="button"
                      disabled={reconcilingClickup}
                      onClick={() => void handleClickupReconciliation()}
                    >
                      <RefreshCw
                        size={14}
                        className={reconcilingClickup ? 'is-spinning' : ''}
                      />
                      {reconcilingClickup ? 'Checking…' : 'Check now'}
                    </button>
                  ) : null}
                </div>
              ) : null}
            </div>
            {mode === 'inbox' ? (
              <div className="workInboxViews" aria-label="Inbox views">
                {(
                  [
                    ['mine', 'Assigned to me', inboxCounts.mine],
                    ['team', 'Needs attention', inboxCounts.team],
                    ['approvals', 'Reviews', inboxCounts.approvals],
                    ['unowned', 'Needs owner', inboxCounts.unowned]
                  ] as Array<[WorkInboxView, string, number]>
                ).map(([key, label, count]) => (
                  <button
                    key={key}
                    type="button"
                    className={inboxView === key ? 'is-active' : ''}
                    aria-pressed={inboxView === key}
                    onClick={() => setInboxView(key)}
                  >
                    <span>{label}</span>
                    <strong>{count}</strong>
                  </button>
                ))}
              </div>
            ) : null}
            {mode === 'inbox' && unmappedIdentities.length ? (
              <details className="workIdentityCoverage">
                <summary>
                  <span>
                    <Link2 size={15} />
                    <span>
                      <strong>Finish identity matching</strong>
                      <small>
                        {unmappedIdentities.length} GitHub or ClickUp account
                        {unmappedIdentities.length === 1 ? ' is' : 's are'} not
                        linked. Link yours to fill Assigned to me.
                      </small>
                    </span>
                  </span>
                  <ChevronDown size={15} />
                </summary>
                <div>
                  {unmappedIdentities.map((identity) => {
                    const label =
                      identity.displayName ||
                      identity.handle ||
                      identity.providerUserId;
                    return (
                      <div key={identity.workIdentityId}>
                        <span>
                          <strong>{label}</strong>
                          <small>
                            {identity.provider === 'github'
                              ? 'GitHub'
                              : identity.provider === 'clickup'
                                ? 'ClickUp'
                                : identity.provider}{' '}
                            · not linked
                          </small>
                        </span>
                        {identity.canClaim ? (
                          <button
                            type="button"
                            disabled={
                              claimingIdentityId === identity.workIdentityId
                            }
                            onClick={() =>
                              void handleClaimIdentity(
                                identity.workIdentityId,
                                label
                              )
                            }
                          >
                            {claimingIdentityId === identity.workIdentityId
                              ? 'Linking…'
                              : 'This is me'}
                          </button>
                        ) : (
                          <small className="workIdentityVerificationPending">
                            Sign in with this provider to link it
                          </small>
                        )}
                      </div>
                    );
                  })}
                </div>
              </details>
            ) : null}
            {mode === 'radar' ? (
              <div className="workRadarSummary" aria-label="Journey views">
                <button
                  type="button"
                  className={`workRadarView--human${view === 'human' ? ' is-active' : ''}`}
                  onClick={() => setView('human')}
                >
                  <strong>{radarSummary.humanAction}</strong>
                  <span>Human gates</span>
                </button>
                <button
                  type="button"
                  className={`workRadarView--failed${view === 'failed' ? ' is-active' : ''}`}
                  onClick={() => setView('failed')}
                >
                  <strong>{radarSummary.failed}</strong>
                  <span>Failed</span>
                </button>
                <button
                  type="button"
                  className={`workRadarView--unlinked${view === 'unlinked' ? ' is-active' : ''}`}
                  onClick={() => setView('unlinked')}
                >
                  <strong>{radarSummary.unlinked}</strong>
                  <span>Unlinked work</span>
                </button>
                <button
                  type="button"
                  className={`workRadarView--stale${view === 'stale' ? ' is-active' : ''}`}
                  onClick={() => setView('stale')}
                >
                  <strong>{radarSummary.stale}</strong>
                  <span>Stale</span>
                </button>
                <button
                  type="button"
                  className={`workRadarView--active${view === 'active' ? ' is-active' : ''}`}
                  onClick={() => setView('active')}
                >
                  <strong>{radarSummary.progressing}</strong>
                  <span>Progressing</span>
                </button>
                <button
                  type="button"
                  className={`workRadarView--complete${view === 'complete' ? ' is-active' : ''}`}
                  onClick={() => setView('complete')}
                >
                  <strong>{radarSummary.delivered}</strong>
                  <span>Delivered or closed</span>
                </button>
                <button
                  type="button"
                  className={`workRadarView--all${view === 'all' ? ' is-active' : ''}`}
                  onClick={() => setView('all')}
                >
                  <strong>{radarItems.length}</strong>
                  <span>All journeys</span>
                </button>
              </div>
            ) : null}
            <div className={`workRadarWorkspace workRadarWorkspace--${mode}`}>
              {mode === 'inbox' ? (
                <aside
                  className="workActionQueue"
                  aria-label="Operational action queue"
                >
                  <div className="workActionQueueHeading">
                    <div>
                      <span>
                        {inboxView === 'mine'
                          ? 'Assigned to me'
                          : inboxView === 'team'
                            ? 'Needs attention'
                            : inboxView === 'approvals'
                              ? 'Reviews'
                              : 'Needs owner'}
                      </span>
                      <strong>
                        {inboxView === 'mine'
                          ? 'Your next steps'
                          : inboxView === 'team'
                            ? 'Fix these first'
                            : inboxView === 'approvals'
                              ? 'Waiting for a decision'
                              : 'Choose who takes this'}
                      </strong>
                    </div>
                    <span>{scopedActionItems.length}</span>
                  </div>
                  {scopedActionItems.length ? (
                    <div className="workActionList">
                      {scopedActionItems.slice(0, visibleLimit).map((item) => {
                        const action = item.nextAction;
                        if (!action) return null;
                        const actionStageKey = action.stageKey;
                        const expectation = item.journey.expectations.find(
                          (candidate) => candidate.stageKey === action.stageKey
                        );
                        const definition = pipelineDefinitions.find(
                          (candidate) =>
                            candidate.pipelineDefinitionId ===
                            expectation?.pipelineDefinitionId
                        );
                        const stage = definition?.stages.find(
                          (candidate) => candidate.stageKey === action.stageKey
                        );
                        const canDispatch =
                          expectation?.status === 'blocked' &&
                          action.waitingOn === 'human' &&
                          stage?.trigger?.on === 'manual' &&
                          Boolean(stage.connectorType && stage.connectorAction);
                        const canAcknowledge =
                          expectation?.status === 'blocked' &&
                          action.waitingOn === 'human' &&
                          !canDispatch;
                        const isPersistedFinding =
                          !action.id.startsWith('coverage:');
                        const OwnerIcon =
                          action.waitingOn === 'human'
                            ? UserRound
                            : action.waitingOn === 'system'
                              ? Bot
                              : AlertTriangle;
                        const assignedResponsibilities =
                          action.responsibilities.filter(
                            responsibilityAssignsWork
                          );
                        const assignedMember = organizationMembers.find(
                          (member) =>
                            member.tenantUserId === action.assignedToUserId
                        );
                        const assignedMemberName =
                          assignedMember?.displayName ||
                          assignedMember?.email ||
                          action.assignedToEmail;
                        const namedOwners = action.requiredOwners.length
                          ? action.requiredOwners
                          : assignedMemberName
                            ? [assignedMemberName]
                            : assignedResponsibilities.map(responsibilityName);
                        const ownershipReason = action.policy
                          ? `${action.policy.sourcePath || 'GitHub review policy'}${action.matchedPaths.length ? ` · ${action.matchedPaths.length} owned path${action.matchedPaths.length === 1 ? '' : 's'}` : ''}`
                          : action.assignedToUserId || action.assignedToEmail
                            ? 'Claimed directly in Cindro'
                            : assignedResponsibilities[0]?.reason;
                        const ownerLabel = action.policy
                          ? 'Required approval'
                          : action.waitingOn === 'system'
                            ? 'Owner'
                            : action.waitingOn === 'external'
                              ? 'Waiting on'
                              : namedOwners.length > 1
                                ? 'Responsible people'
                                : 'Responsible';
                        const ownerNames = action.policy
                          ? namedOwners.join(', ') || 'Repository reviewers'
                          : action.waitingOn === 'system'
                            ? namedOwners.join(', ') || 'Unassigned'
                            : namedOwners.length
                              ? namedOwners.join(', ')
                              : action.waitingOn === 'external'
                                ? 'External provider'
                                : 'Unowned';
                        const ownerContext =
                          action.policy && assignedMemberName
                            ? `Assigned to ${assignedMemberName} · ${ownershipReason || 'GitHub review policy'}`
                            : ownershipReason;
                        return (
                          <article
                            key={action.id}
                            className={`workActionItem workActionItem--${action.severity}`}
                          >
                            <button
                              type="button"
                              className="workActionSummary"
                              onClick={() =>
                                inspectJourney(item.journey.workJourneyId)
                              }
                            >
                              <span className="workActionOwner">
                                <OwnerIcon size={12} />
                                {action.stageLabel} · {actionStateLabel(action)}{' '}
                                · {formatRelativeDate(action.firstDetectedAt)}
                              </span>
                              <strong>{item.journey.title}</strong>
                              <span className="workActionProblem">
                                {action.cause ||
                                  formatAttentionDetail(action.detail)}
                              </span>
                              <small>{action.target}</small>
                              {item.journey.taskRefs.length ? (
                                <small className="workActionTaskRefs">
                                  Tasks{' '}
                                  {item.journey.taskRefs.slice(0, 3).join(', ')}
                                  {item.journey.taskRefs.length > 3
                                    ? ` +${item.journey.taskRefs.length - 3}`
                                    : ''}
                                </small>
                              ) : null}
                              <span className="workActionNext">
                                <b>Next</b>
                                {actionRecommendation(action)}
                              </span>
                              <span
                                className={`workActionResponsibility${namedOwners.length ? '' : ' is-unowned'}`}
                              >
                                <UserRound size={14} />
                                <span>
                                  <b>{ownerLabel}</b>
                                  <strong>{ownerNames}</strong>
                                  {ownerContext ? (
                                    <small>{ownerContext}</small>
                                  ) : null}
                                </span>
                              </span>
                            </button>
                            <div className="workActionControls">
                              {isPersistedFinding &&
                              !action.assignedToUserId &&
                              !action.assignedToEmail &&
                              !namedOwners.length ? (
                                <button
                                  type="button"
                                  className="workActionPrimary"
                                  disabled={triagingFindingId === action.id}
                                  onClick={() =>
                                    void handleTriageFinding(
                                      action.id,
                                      'assign_self'
                                    )
                                  }
                                >
                                  {triagingFindingId === action.id
                                    ? 'Taking ownership…'
                                    : 'Take ownership'}
                                </button>
                              ) : null}
                              {action.remediation === 'rerun_failed_jobs' ? (
                                <button
                                  type="button"
                                  className="workActionPrimary"
                                  disabled={
                                    remediatingItemId === action.evidenceItemId
                                  }
                                  onClick={() => void handleRemediation(item)}
                                >
                                  <RefreshCw
                                    size={13}
                                    className={
                                      remediatingItemId ===
                                      action.evidenceItemId
                                        ? 'is-spinning'
                                        : ''
                                    }
                                  />
                                  {remediatingItemId === action.evidenceItemId
                                    ? 'Starting…'
                                    : 'Rerun failed jobs'}
                                </button>
                              ) : null}
                              {canDispatch && actionStageKey ? (
                                <button
                                  type="button"
                                  className="workActionPrimary"
                                  disabled={
                                    dispatchingKey ===
                                    `${item.journey.workJourneyId}:${actionStageKey}`
                                  }
                                  onClick={() =>
                                    handleDispatch(
                                      item.journey,
                                      actionStageKey,
                                      action.stageLabel
                                    )
                                  }
                                >
                                  <CirclePlay size={12} />
                                  Run step
                                </button>
                              ) : null}
                              {canAcknowledge && actionStageKey ? (
                                <button
                                  type="button"
                                  className="workActionPrimary"
                                  onClick={() =>
                                    handleAcknowledge(
                                      item.journey,
                                      actionStageKey,
                                      action.stageLabel
                                    )
                                  }
                                >
                                  Record done
                                </button>
                              ) : null}
                              {action.url ? (
                                <a
                                  className="workActionDirect"
                                  href={action.url}
                                  target="_blank"
                                  rel="noreferrer"
                                  aria-label={`${actionLinkLabel(action)} for ${item.journey.title}`}
                                >
                                  {actionLinkLabel(action)}
                                  <ArrowUpRight size={13} />
                                </a>
                              ) : null}
                              {!canDispatch && !canAcknowledge ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    inspectJourney(item.journey.workJourneyId)
                                  }
                                >
                                  {expandedJourneyId ===
                                  item.journey.workJourneyId
                                    ? 'Hide details'
                                    : 'Show details'}
                                </button>
                              ) : null}
                              {isPersistedFinding ? (
                                <details className="workActionManage">
                                  <summary
                                    aria-label={`Manage finding for ${item.journey.title}`}
                                  >
                                    <MoreHorizontal size={14} />
                                  </summary>
                                  <div>
                                    {!action.assignedToUserId &&
                                    !action.assignedToEmail &&
                                    namedOwners.length ? (
                                      <button
                                        type="button"
                                        disabled={
                                          triagingFindingId === action.id
                                        }
                                        onClick={() =>
                                          handleTriageFinding(
                                            action.id,
                                            'assign_self'
                                          )
                                        }
                                      >
                                        Take ownership
                                      </button>
                                    ) : null}
                                    {organizationMembers.length ? (
                                      <label className="workActionAssign">
                                        <span>Assign to a teammate</span>
                                        <select
                                          defaultValue=""
                                          disabled={
                                            triagingFindingId === action.id
                                          }
                                          onChange={(event) => {
                                            if (event.target.value)
                                              void handleTriageFinding(
                                                action.id,
                                                'assign_user',
                                                event.target.value
                                              );
                                          }}
                                        >
                                          <option value="">
                                            Choose a person
                                          </option>
                                          {organizationMembers.map((member) => (
                                            <option
                                              key={member.tenantUserId}
                                              value={member.tenantUserId}
                                            >
                                              {member.displayName ||
                                                member.email ||
                                                'Workspace member'}
                                            </option>
                                          ))}
                                        </select>
                                      </label>
                                    ) : null}
                                    {!action.acknowledgedAt ? (
                                      <button
                                        type="button"
                                        disabled={
                                          triagingFindingId === action.id
                                        }
                                        onClick={() =>
                                          handleTriageFinding(
                                            action.id,
                                            'acknowledge'
                                          )
                                        }
                                      >
                                        Acknowledge
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      disabled={triagingFindingId === action.id}
                                      onClick={() =>
                                        handleTriageFinding(action.id, 'snooze')
                                      }
                                    >
                                      Snooze 1 day
                                    </button>
                                    <button
                                      type="button"
                                      disabled={triagingFindingId === action.id}
                                      onClick={() =>
                                        handleTriageFinding(
                                          action.id,
                                          'resolve'
                                        )
                                      }
                                    >
                                      Resolve finding
                                    </button>
                                  </div>
                                </details>
                              ) : null}
                            </div>
                            {expandedJourneyId ===
                            item.journey.workJourneyId ? (
                              <JourneyDetail
                                journey={item.journey}
                                dispatchableStageKeys={
                                  new Set(
                                    pipelineDefinitions
                                      .find(
                                        (candidate) =>
                                          candidate.pipelineDefinitionId ===
                                          expectation?.pipelineDefinitionId
                                      )
                                      ?.stages.filter(
                                        (candidate) =>
                                          candidate.trigger?.on === 'manual' &&
                                          candidate.connectorType &&
                                          candidate.connectorAction
                                      )
                                      .map((candidate) => candidate.stageKey) ||
                                      []
                                  )
                                }
                                dispatchingStageKey={
                                  dispatchingKey.startsWith(
                                    `${item.journey.workJourneyId}:`
                                  )
                                    ? dispatchingKey.split(':')[1]
                                    : undefined
                                }
                                onAcknowledge={(stageKey, label) =>
                                  handleAcknowledge(
                                    item.journey,
                                    stageKey,
                                    label
                                  )
                                }
                                onDispatch={(stageKey, label) =>
                                  handleDispatch(item.journey, stageKey, label)
                                }
                              />
                            ) : null}
                          </article>
                        );
                      })}
                      {scopedActionItems.length > visibleLimit ? (
                        <button
                          type="button"
                          className="workActionMore"
                          onClick={() => setVisibleLimit((limit) => limit + 12)}
                        >
                          Show{' '}
                          {Math.min(
                            12,
                            scopedActionItems.length - visibleLimit
                          )}{' '}
                          more
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <div className="workActionEmpty">
                      <span>
                        <span aria-hidden="true" />
                        {inboxView === 'mine' ? 'Nothing assigned' : 'Clear'}
                      </span>
                      <strong>
                        {inboxView === 'mine' && inboxCounts.team
                          ? 'Cindro cannot match any open work to you yet'
                          : 'Nothing needs action here'}
                      </strong>
                      <p>
                        {inboxView === 'mine' && inboxCounts.team
                          ? `${inboxCounts.team} item${inboxCounts.team === 1 ? '' : 's'} still need attention. Link your GitHub or ClickUp identity, or open Needs attention to help now.`
                          : 'New failures, approvals, and overdue work will appear here.'}
                      </p>
                      {inboxView === 'mine' && inboxCounts.team ? (
                        <button
                          type="button"
                          onClick={() => setInboxView('team')}
                        >
                          Open needs attention
                        </button>
                      ) : null}
                    </div>
                  )}
                </aside>
              ) : null}
              {mode === 'radar' ? (
                <div className="workRadarResults">
                  <div className="workRadarResultsHeading">
                    <strong>
                      {visibleItems.length}{' '}
                      {visibleItems.length === 1 ? 'journey' : 'journeys'}
                      {stageFilter ? ` at ${stageFilterLabel}` : ''}
                    </strong>
                    {stageFilter ? (
                      <button type="button" onClick={() => setStageFilter('')}>
                        <span>Stage filter</span>
                        {stageFilterLabel}
                        <X size={13} />
                      </button>
                    ) : (
                      <span>Most urgent first</span>
                    )}
                  </div>
                  {visibleItems.length ? (
                    <div
                      className="journeyList"
                      aria-label="Development journeys"
                    >
                      {displayedItems.map((item) => {
                        const projectNames = item.journey.projectIds
                          .map((id) => apps.find((app) => app.id === id)?.name)
                          .filter((name): name is string => Boolean(name));
                        const pipelineDefinitionId =
                          item.journey.expectations.find(
                            (expectation) => expectation.pipelineDefinitionId
                          )?.pipelineDefinitionId;
                        const definition = pipelineDefinitions.find(
                          (candidate) =>
                            candidate.pipelineDefinitionId ===
                            pipelineDefinitionId
                        );
                        const dispatchableStageKeys = new Set(
                          definition?.stages
                            .filter(
                              (stage) =>
                                stage.trigger?.on === 'manual' &&
                                stage.connectorType &&
                                stage.connectorAction
                            )
                            .map((stage) => stage.stageKey) || []
                        );
                        return (
                          <JourneyResultRow
                            key={item.journey.workJourneyId}
                            item={item}
                            taskItems={taskItems}
                            onCreateTaskRelationship={
                              handleCreateTaskRelationship
                            }
                            onDeleteTaskRelationship={
                              handleDeleteTaskRelationship
                            }
                            projectNames={projectNames}
                            expanded={
                              expandedJourneyId === item.journey.workJourneyId
                            }
                            attentionDetail={formatAttentionDetail(
                              item.attentionDetail
                            )}
                            dispatchableStageKeys={dispatchableStageKeys}
                            dispatchingStageKey={
                              dispatchingKey.startsWith(
                                `${item.journey.workJourneyId}:`
                              )
                                ? dispatchingKey.split(':')[1]
                                : undefined
                            }
                            onToggle={() =>
                              setExpandedJourneyId((current) =>
                                current === item.journey.workJourneyId
                                  ? ''
                                  : item.journey.workJourneyId
                              )
                            }
                            onAcknowledge={(stageKey, label) =>
                              handleAcknowledge(item.journey, stageKey, label)
                            }
                            onDispatch={(stageKey, label) =>
                              handleDispatch(item.journey, stageKey, label)
                            }
                          />
                        );
                      })}
                      {visibleItems.length > displayedItems.length ? (
                        <button
                          type="button"
                          className="journeyShowMore"
                          onClick={() => setVisibleLimit((limit) => limit + 12)}
                        >
                          Show 12 more ·{' '}
                          {visibleItems.length - displayedItems.length}{' '}
                          remaining
                        </button>
                      ) : null}
                    </div>
                  ) : (
                    <EmptyState
                      compact
                      icon={<Search size={18} />}
                      title="No journeys match this view"
                      description={
                        stageFilter
                          ? `No tracked journey currently includes ${stageFilterLabel}. Choose another stage on the map or clear the filter.`
                          : 'Change the attention filter or search terms.'
                      }
                      action={
                        <button
                          type="button"
                          className="secondaryButton"
                          onClick={() => {
                            setQuery('');
                            setProjectFilter('all');
                            setStageFilter('');
                            setView('all');
                          }}
                        >
                          Show all journeys
                        </button>
                      }
                    />
                  )}
                </div>
              ) : null}
            </div>
          </>
        ) : (
          <EmptyState
            compact
            icon={<GitMerge size={20} />}
            title="No development journeys connected"
            description="Connect source, delivery, and infrastructure repositories in Delivery model. Task-manager references are already part of the journey model and can be connected later."
          />
        )}
      </SectionCard>
    </>
  );
}

export { PipelineRunsPage };
